/**
 * Step 3 of 7 — Your business. The only typed step: business name + website are
 * enough to draft the genie's brain. The genie type chosen in step 2 carries
 * over as a confirmed chip so the flow feels continuous. Phone is optional and
 * deferred. Values persist to the onboarding draft via setBusiness.
 *
 * Brand law: genie tokens only; signature gradient on chip accent; *Voice* lives
 * in the frame wordmark; no "AI" language; no em dashes. Lucide for inline icons.
 */
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Building2, Globe, Phone, Check } from "lucide-react";
import { WelcomeFrame } from "@/components/voice-onboarding/WelcomeFrame";
import { useOnboarding } from "@/contexts/voice-onboarding/OnboardingContext";
import type { OnboardingBusiness } from "@/types/voice-vision";
import { ONBOARD_ROUTES, VOICE_ONBOARD_STEPS } from "./steps";
import { OnbField } from "./BusinessFields";

interface BusinessForm {
  name: string;
  website: string;
}

const BusinessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { genieType, business, setBusiness } = useOnboarding();

  // Landing pages send `helpgenie.ai/signup?url=theirsite.com`; SignUp carries
  // it through to here as `?url=` (or `?website=`) so the site the visitor
  // built from is already in the box, not retyped.
  const prefillWebsite = searchParams.get("url") ?? searchParams.get("website") ?? "";

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<BusinessForm>({
    mode: "onChange",
    defaultValues: {
      name: business.name ?? "",
      website: business.website || prefillWebsite,
    },
  });

  const name = watch("name");
  const website = watch("website");
  const canContinue = Boolean(name?.trim()) && Boolean(website?.trim());

  const onSubmit = (values: BusinessForm) => {
    const next: OnboardingBusiness = {
      ...business,
      name: values.name.trim(),
      website: values.website.trim(),
    };
    setBusiness(next);
    navigate(ONBOARD_ROUTES[3]); // /welcome/building
  };

  // Skip keeps whatever was typed and moves on. The build step already handles
  // both gaps: no website marks the knowledge stage as skipped, no name falls
  // back to a generic genie name in OnboardingLaunchService.
  const onSkipStep = () => {
    setBusiness({
      ...business,
      name: name?.trim() ?? "",
      website: website?.trim() ?? "",
    });
    navigate(ONBOARD_ROUTES[3]); // /welcome/building
  };

  const typeTitle = genieType?.title ?? "your genie";

  return (
    <WelcomeFrame
      step={3}
      steps={VOICE_ONBOARD_STEPS}
      mascot={genieType?.mascot ?? "receptionist"}
      eyebrow="ABOUT YOUR BUSINESS"
      title="Tell us who it's for"
      subtitle="Two quick things and we'll draft your genie's brain, its job, its greeting, and what it knows. You'll be able to change all of it."
      continueLabel="Build my genie brain"
      maxWidth={520}
      continueDisabled={!canContinue}
      onBack={() => navigate(ONBOARD_ROUTES[1])} // /welcome/pick
      onContinue={handleSubmit(onSubmit)}
      onSkip={() => navigate("/genies")}
      onSkipStep={onSkipStep}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        data-hg-block="business-form"
        className="w-full flex flex-col gap-5"
      >
        {/* carried-over genie type from step 2 */}
        <div className="self-start inline-flex items-center gap-2 h-[30px] pl-2 pr-3 rounded-full bg-genie-primary/10 border border-genie-primary/30">
          <span className="w-[18px] h-[18px] rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] flex items-center justify-center flex-shrink-0">
            <Check className="w-[11px] h-[11px] text-white" strokeWidth={3} />
          </span>
          <span className="text-[12.5px] font-semibold text-genie-primary">
            {genieType ? `Building a ${typeTitle}` : "Building your genie"}
          </span>
        </div>

        <OnbField
          icon={Building2}
          label="Business name"
          placeholder="e.g. Northside Dental"
          autoFocus
          autoComplete="organization"
          error={errors.name?.message}
          {...register("name", { required: "Add your business name to continue." })}
        />

        <OnbField
          icon={Globe}
          label="Website"
          placeholder="e.g. northsidedental.co.nz"
          inputMode="url"
          autoComplete="url"
          hint="We'll read your site to give your genie its knowledge."
          error={errors.website?.message}
          {...register("website", { required: "Add your website so we can draft the knowledge." })}
        />

        {/* optional, deferred phone to forward */}
        <div className="flex flex-col gap-1.5">
          <label className="text-[12.5px] font-semibold text-[#475569]">
            Phone to forward <span className="text-gray-400 font-medium">(optional)</span>
          </label>
          <div className="h-[50px] rounded-xl bg-genie-background/60 border border-dashed border-gray-300 flex flex-row items-center gap-2.5 px-3.5">
            <Phone className="w-[18px] h-[18px] text-gray-400 flex-shrink-0" strokeWidth={1.8} />
            <span className="flex-1 text-[14px] text-gray-400">
              Add later, we'll give you a number too
            </span>
          </div>
        </div>
      </form>
    </WelcomeFrame>
  );
};

export default BusinessPage;
